import axios from "axios";
import api from "../api";
import apiClient from "../axios";
import { DecryptService } from "../decrypt";

const DEFAULT_TIMEOUT = 30000;

const inferMimeTypeFromUrl = (url: string): string => {
    const cleanUrl = String(url || "").toLowerCase().split("?")[0].split("#")[0];
    const withoutEnc = cleanUrl.endsWith(".enc") ? cleanUrl.slice(0, -4) : cleanUrl;

    if (withoutEnc.endsWith(".jpg") || withoutEnc.endsWith(".jpeg")) return "image/jpeg";
    if (withoutEnc.endsWith(".png")) return "image/png";
    if (withoutEnc.endsWith(".webp")) return "image/webp";
    if (withoutEnc.endsWith(".gif")) return "image/gif";
    if (withoutEnc.endsWith(".bmp")) return "image/bmp";
    if (withoutEnc.endsWith(".svg")) return "image/svg+xml";

    return "image/jpeg";
};

const normalizeBlobMimeType = (headerMimeType: string | undefined, imageUrl: string): string => {
    const mime = (headerMimeType || "").toLowerCase();
    if (!mime || mime.includes("octet-stream")) {
        return inferMimeTypeFromUrl(imageUrl);
    }
    return mime;
};

const collapseRepeatedPathSegment = (url: string): string => {
    try {
        const parsed = new URL(url)
        const parts = parsed.pathname.split('/').filter(Boolean)
        const collapsed: string[] = []
        for (const part of parts) {
            if (collapsed[collapsed.length - 1] !== part) collapsed.push(part)
        }
        parsed.pathname = `/${collapsed.join('/')}`
        return parsed.toString()
    } catch {
        return url
    }
}

const resolveStorageUrl = (imageUrl: string): string => {
    const overrideStorageBaseUrl = (
        import.meta.env.VITE_STORAGE_BASE_URL ||
        import.meta.env.VITE_MEDIA_STORAGE_BASE_URL ||
        ""
    ).replace(/\/$/, "")

    let url = imageUrl
    if (overrideStorageBaseUrl) {
        url = url.replace("https://lldvod.xosp.tv", overrideStorageBaseUrl)
    }

    return collapseRepeatedPathSegment(url)
};

const imageBlobCache = new Map<string, Blob>()
const imageBlobInFlight = new Map<string, Promise<Blob>>()

const MAX_CONCURRENT_FRONT_DECRYPTS = 3
let frontDecryptsInFlight = 0
const frontDecryptWaitQueue: Array<() => void> = []

const acquireFrontDecryptSlot = async () => {
    if (frontDecryptsInFlight < MAX_CONCURRENT_FRONT_DECRYPTS) {
        frontDecryptsInFlight += 1
        return
    }
    await new Promise<void>((resolve) => {
        frontDecryptWaitQueue.push(resolve)
    })
    frontDecryptsInFlight += 1
}

const releaseFrontDecryptSlot = () => {
    frontDecryptsInFlight = Math.max(0, frontDecryptsInFlight - 1)
    const next = frontDecryptWaitQueue.shift()
    if (next) next()
}

export type EncryptedImageOptions = {
    signal?: AbortSignal
}

export function getCachedImageBlob(imageUrl: string): Blob | undefined {
    return imageBlobCache.get(imageUrl)
}

const runWithImageDedup = async (
    imageUrl: string,
    worker: () => Promise<Blob>,
): Promise<Blob> => {
    const cachedBlob = imageBlobCache.get(imageUrl)
    if (cachedBlob) {
        return cachedBlob
    }

    const inFlight = imageBlobInFlight.get(imageUrl)
    if (inFlight) {
        return inFlight
    }

    const decryptPromise = worker()
    imageBlobInFlight.set(imageUrl, decryptPromise)

    try {
        const blob = await decryptPromise
        imageBlobCache.set(imageUrl, blob)
        return blob
    } finally {
        imageBlobInFlight.delete(imageUrl)
    }
}

export async function CDNImage(imageUrl: string, options: EncryptedImageOptions = {}) {
    return runWithImageDedup(imageUrl, async () => {
    try {
        // Prefer impersonation token when present, then fallback to admin token.
        const token =
            sessionStorage.getItem('impersonation_token') ||
            sessionStorage.getItem('admin_token') ||
            localStorage.getItem('impersonation_token') ||
            localStorage.getItem('admin_token');
        
        // Validation du token
        if (!token) {
            console.error('Aucun token d\'authentification trouvé pour CDNImage');
            throw new Error('Token d\'authentification manquant');
        }
        
        // Validation de l'URL
        if (!imageUrl || imageUrl.trim() === '') {
            console.error('URL de l\'image vide ou invalide:', imageUrl);
            throw new Error('URL de l\'image invalide');
        }
        
        const response = await axios.post(api.playphoto(), {url: imageUrl}, {
            responseType: "blob",
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            timeout: DEFAULT_TIMEOUT,
            signal: options.signal,
        });
        
        if (!response.data) {
            throw new Error('Aucune donnée reçue du serveur');
        }
        
        // Validation du blob reçu
        if (!(response.data instanceof Blob)) {
            console.error('La réponse n\'est pas un blob:', typeof response.data);
            throw new Error('Format de réponse invalide du serveur');
        }
        
        // Vérifier si le blob est valide
        if (response.data.size === 0) {
            throw new Error('Image vide reçue du serveur');
        }

        return response.data;
    } catch (error: any) {
        if (axios.isCancel(error)) {
            throw error;
        }

        console.error('Erreur lors du décryptage de l\'image CDN:', error);
        
        // Gérer les erreurs spécifiques de décodage
        if (error.message && error.message.includes('wrong final block length')) {
            console.error('Erreur de décodage détectée, tentative avec URL normale');
            throw new Error('Erreur de décodage de l\'image - format non supporté');
        }
        
        // Gérer les différents types d'erreurs
        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.response.data?.error || 'Erreur serveur';
            
            switch (status) {
                case 401:
                    throw new Error('Non autorisé - token invalide ou expiré');
                case 403:
                    throw new Error('Accès refusé - permissions insuffisantes');
                case 404:
                    throw new Error('Image non trouvée');
                case 413:
                    throw new Error('Image trop volumineuse');
                case 415:
                    throw new Error('Format d\'image non supporté');
                case 500:
                    throw new Error(`Erreur serveur interne: ${message}`);
                default:
                    throw new Error(`Erreur HTTP ${status}: ${message}`);
            }
        } else if (error.request) {
            throw new Error('Erreur réseau - impossible de contacter le serveur');
        } else {
            throw new Error(`Erreur de décryptage: ${error.message}`);
        }
    }
    })
}

const normalizeStorageOrigin = (value: string): string => {
    const raw = String(value || "").trim().replace(/\/$/, "")
    if (!raw) return ""
    try {
        const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
        return new URL(withProtocol).origin
    } catch {
        return ""
    }
}

let cachedStorageOrigin = ""
let storageOriginInFlight: Promise<string> | null = null

const fetchStorageOrigin = async (): Promise<string> => {
    const envOrigin = normalizeStorageOrigin(
        import.meta.env.VITE_STORAGE_BASE_URL ||
        import.meta.env.VITE_MEDIA_STORAGE_BASE_URL ||
        "",
    )
    if (envOrigin) return envOrigin

    try {
        const { data } = await apiClient.post(api.listDomains(), {
            keyword: "storage",
            limit: 50,
            isActive: 1,
        })
        const storage = (data?.list || []).find(
            (item: any) => String(item?.tag || "").toLowerCase() === "storage" && item?.domain,
        )
        return normalizeStorageOrigin(storage?.domain || "")
    } catch {
        return ""
    }
}

const getStorageOrigin = async (): Promise<string> => {
    if (cachedStorageOrigin) return cachedStorageOrigin
    if (storageOriginInFlight) return storageOriginInFlight

    storageOriginInFlight = fetchStorageOrigin().finally(() => {
        storageOriginInFlight = null
    })
    cachedStorageOrigin = await storageOriginInFlight
    return cachedStorageOrigin
}

const buildDecryptCandidateUrls = async (imageUrl: string): Promise<string[]> => {
    const primary = resolveStorageUrl(imageUrl)
    const urls = [primary]
    try {
        const parsed = new URL(primary)
        const storageOrigin = await getStorageOrigin()
        if (storageOrigin && parsed.origin !== storageOrigin) {
            urls.push(`${storageOrigin}${parsed.pathname}${parsed.search}`)
        }
    } catch {
        return urls
    }
    return [...new Set(urls)]
}

export async function CDNImageFrontDecrypt(
    imageUrl: string,
    options: EncryptedImageOptions = {},
): Promise<Blob> {
    if (!imageUrl || imageUrl.trim() === "") {
        throw new Error("URL de l'image invalide");
    }

    return runWithImageDedup(imageUrl, async () => {
        await acquireFrontDecryptSlot()
        try {
            const decryptUtils = new DecryptService();
            const candidates = await buildDecryptCandidateUrls(imageUrl)
            let lastError: unknown

            for (const finalUrl of candidates) {
                try {
                    const response = await axios.get(finalUrl, {
                        responseType: "arraybuffer",
                        timeout: DEFAULT_TIMEOUT,
                        signal: options.signal,
                    });

                    const encryptedBytes = new Uint8Array(response.data);
                    const decryptedBytes = await decryptUtils.decryptBuffer(encryptedBytes);
                    const mimeType = normalizeBlobMimeType(response.headers?.["content-type"], imageUrl);
                    const decryptedArrayBuffer = decryptedBytes.buffer.slice(
                        decryptedBytes.byteOffset,
                        decryptedBytes.byteOffset + decryptedBytes.byteLength,
                    ) as ArrayBuffer;

                    return new Blob([decryptedArrayBuffer], { type: mimeType });
                } catch (error: any) {
                    lastError = error
                    if (error?.response?.status !== 404) throw error
                }
            }

            throw lastError
        } finally {
            releaseFrontDecryptSlot()
        }
    })
}

export async function resolveEncryptedImageBlob(
    imageUrl: string,
    options: EncryptedImageOptions = {},
): Promise<Blob> {
    return CDNImageFrontDecrypt(imageUrl, options)
}
